/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { Observable, from, of } from 'rxjs';
import { v4 as uuidV4 } from 'uuid';
import {
  map,
  take,
  switchMap,
  catchError,
  shareReplay,
  timeout,
  tap,
  first
} from 'rxjs/operators';
import { ExperimentClient } from '@amplitude/experiment-js-client';

import { AnalyticsBrowser } from '@segment/analytics-next';
import { ConfigurationService, IAmplitudeAppConfig } from '@sondermind/configuration';
import { CurrentUser } from '@sondermind/data-access/client-user';
import { SentryClient } from '@sondermind/sentry-client';
import { Logger } from '@sondermind/datadog-logging';
import { AmplitudeFeatureFlags } from '../models/amplitude-feature-flags.enum';
import {
  EvaluationContext,
  FeatureFlagVariant
} from '../models/amplitude-feature-flag.types';

// Amplitude FF service setup according to SDK guide:
// https://www.docs.developers.amplitude.com/experiment/sdks/javascript-sdk/#configuration

// Default timeout for fetch requests
const DEFAULT_TIMEOUT = 3000;
const LOCAL_DEV_SERVER_URL = 'https://api.lab.amplitude.com';

// This is the default server URL for the Experiment SDK
// It's not exported from the SDK, so definining it here for clarity
const EXPERIMENT_SDK_DEFAULT_SERVER_URL = 'https://api.lab.amplitude.com';

interface IEvaluationOptions {
  evaluateUser?: IExperimentUser;
  timeout?: number;
  forceRefresh?: boolean;
}

export interface IExperimentUser {
  user_id: string;
  device_id?: string;
  user_properties?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class AmplitudeFeatureFlagService {
  private experimentClient?: ExperimentClient;
  private analytics?: AnalyticsBrowser;
  private initialized = false;
  private deviceId: string;

  constructor(
    private configurationService: ConfigurationService<IAmplitudeAppConfig>,
    private currentUser: CurrentUser,
    private logger: Logger,
    private http: HttpClient
  ) { }

  initialize$(): Observable<boolean> {
    if (this.initialized) {
      return of(true);
    }

    const config = this.configurationService.env.app;
    if (!config?.amplitudeDeploymentKey) {
      return of(false);
    }

    const isLocalDevelopment = window.location.hostname === 'localhost';

    // uncomment debug property for amplitude console logging
    this.experimentClient = new ExperimentClient(config.amplitudeDeploymentKey, {
      // debug: this.configurationService.env.sentryDebug,
      serverUrl: isLocalDevelopment ? LOCAL_DEV_SERVER_URL : undefined
    });

    return this.anonymousId$().pipe(
      tap((anonymousId) => {
        // test connection to amplitude by directly calling the API
        // NB: this is just a rudimentary connection test; it's not used for anything else

        this.http.get(
          `${EXPERIMENT_SDK_DEFAULT_SERVER_URL}/v1/vardata?&device_id=${anonymousId}`,
          { headers: { Authorization: `Api-Key ${config.amplitudeDeploymentKey}` } }
        ).pipe(
          first(),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 0 && error.statusText === 'Unknown Error') {
              // status 0 AND statusText "Unknown Error" means we couldn't connect to the server
              // this is most likely due to the request getting blocked (e.g. CORS, firewall, ad blocker, etc.)
              // we can't do anything about it here, so we'll just log it
              this.logger.warn('Unable to reach Amplitude.', { deviceId: anonymousId });
            }

            // return value is irrelevant
            return of();
          })
        ).subscribe();
      }),
      switchMap((anonymousId) => {
        this.deviceId = anonymousId;
        this.initialized = true;
        return of(true);
      }),
      catchError(
        (error) => {
          SentryClient.captureException(error);
          return of(false);
        }
      )
    );
  }

  evaluate$<TKey extends string = string, TPayload = unknown>(
    flagKey: AmplitudeFeatureFlags,
    defaultValue: TPayload,
    options?: IEvaluationOptions
  ): Observable<FeatureFlagVariant<TKey, TPayload> | null> {
    return this.initialize$().pipe(
      switchMap((initialized) => {
        if (!initialized || !this.experimentClient) {
          return of(null);
        }

        return (this.currentUser.user$ ?? of({ platformAccountUuid: 'anonymous' })).pipe(
          take(1),
          switchMap((currentUser) => {
            const amplitudeUser = {
              device_id: this.deviceId
            };

            if (!!currentUser?.platformAccountUuid && currentUser?.platformAccountUuid !== 'anonymous') {
              amplitudeUser['user_id'] = currentUser?.platformAccountUuid;
            }

            return from(this.experimentClient.fetch(amplitudeUser)).pipe(
              switchMap(() => this.evaluateVariant$<TKey, TPayload>(flagKey, defaultValue)),
              timeout(options?.timeout ?? DEFAULT_TIMEOUT)
            );
          })
        );
      }),
      catchError((error: unknown) => {
        SentryClient.captureException(error);
        this.logger.error(`Error evaluating feature flag ${flagKey}. Returning default value.`, { error });
        return of({ key: 'off', payload: defaultValue } as FeatureFlagVariant<TKey, TPayload>);
      }),
      shareReplay(1)
    );
  }

  /**
   * Returns an observable that emits the boolean value of the feature flag.
   *
   * @param flagKey - The key of the feature flag to evaluate.
   * @param defaultValue - The default value to return if the feature flag is not found or cannot be evaluated.
   * @returns An observable that emits the boolean value of the feature flag.
   *
   * Note: `false` is the implicit default value
   */
  isEnabled$(flagKey: AmplitudeFeatureFlags, defaultValue = false): Observable<boolean> {
    return this.evaluate$<string, boolean>(flagKey, defaultValue).pipe(
      map((variant) => ['on', 'true', 'enabled'].includes(variant?.key ?? ''))
    );
  }

  private evaluateVariant$<TKey extends string = string, TPayload = unknown>(
    flagKey: string,
    defaultValue: TPayload
  ): Observable<FeatureFlagVariant<TKey, TPayload> | null> {
    if (!this.experimentClient) {
      // this should never happen, but just in case
      this.logger.warn(`Experiment client not initialized. Returning default value.`, { flagKey, defaultValue });
      return of({ key: 'off', payload: defaultValue } as FeatureFlagVariant<TKey, TPayload>);
    }

    try {
      const variant = this.experimentClient.variant(flagKey);

      if (!variant?.key) {
        this.logger.warn(`Variant not found for flag ${flagKey}. Returning default value.`, { flagKey, defaultValue });
        return of({ key: 'off', payload: defaultValue } as FeatureFlagVariant<TKey, TPayload>);
      }

      return of({
        key: variant.key as TKey,
        payload: variant.payload as TPayload
      });
    } catch (error: unknown) {
      SentryClient.captureException(error);
      this.logger.error(
        `Error evaluating variant for flag ${flagKey}. Returning default value.`,
        { error, deviceId: this.deviceId }
      );
      return of({ key: 'off', payload: defaultValue } as FeatureFlagVariant<TKey, TPayload>);
    }
  }

  getVariant$<TKey extends string = string, TPayload = unknown>(
    context: EvaluationContext,
    flagKey: string,
    defaultValue: TPayload = null as TPayload
  ): Observable<FeatureFlagVariant<TKey, TPayload> | null> {
    return this.getVariantsForUser$(context.accountId).pipe(
      map((variants) => {
        const variant = variants[flagKey];
        // If variant is undefined, it means the experiment is not active, so return the default value.
        if (
          !variant?.key ||
          variant.key === 'off' ||
          variant.key === 'disabled' ||
          variant.key === 'false'
        ) {
          return { key: 'off', payload: defaultValue } as FeatureFlagVariant<TKey, TPayload>;
        }

        return {
          key: variant.key as TKey,
          payload: variant.payload as TPayload
        };
      }),
      catchError((error: unknown) => {
        SentryClient.captureException(error);
        this.logger.error(
          `Error getting variant for flag ${flagKey}. Returning default value.`,
          { error, deviceId: this.deviceId }
        );
        return of({ key: 'off', payload: defaultValue } as FeatureFlagVariant<TKey, TPayload>);
      })
    );
  }

  private getVariantsForUser$(userId: string): Observable<Record<string, FeatureFlagVariant>> {
    return this.initialize$().pipe(
      switchMap((initialized) => {
        if (!initialized || !this.experimentClient) return of({});

        return from(this.experimentClient.fetch({ user_id: userId })).pipe(
          map((variants) => Object.entries(variants).reduce((acc, [key, variant]: [string, FeatureFlagVariant]) => {
            // Skip variants with undefined keys
            if (!variant?.key) return acc;

            acc[key] = {
              key: variant.key,
              payload: variant.payload
            };
            return acc;
          }, {} as Record<string, FeatureFlagVariant>)),
          catchError(() => of({}))
        );
      })
    );
  }

  anonymousId$(): Observable<string | undefined> {
    if (!this.configurationService.env.app?.segmentWriteKey) {
      SentryClient.captureMessage('No segment write key was provided for the amplitude feature flag service. ' +
        'Continuing by using a generated device ID');
    } else {
      this.analytics = AnalyticsBrowser.load(
        { writeKey: this.configurationService.env.app?.segmentWriteKey }
      );
    }

    if (!this.analytics) {
      return of(uuidV4());
    }

    return from(this.analytics.user()).pipe(
      switchMap((analyticsUser) => {
        const anonymousId = analyticsUser?.anonymousId();
        return of(anonymousId);
      }),
      catchError((error) => {
        SentryClient.captureException(error);
        return of(uuidV4());
      })
    );
  }
}
